import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Label, RevealOnScroll, Button } from '../Common/UI';
import Icons from '../Icons';

const AnatomyFeature = ({ icon, title, desc, align = 'left', className = '' }) => {
    const isRightAligned = align === 'right';
    const iconBg = 'bg-stone-100';
    const iconColor = 'text-stone-500';
    const textClass = isRightAligned ? 'anatomy-text-left' : 'anatomy-text-right';

    return (
        <div className={`anatomy-feature-item flex items-start gap-4 md:gap-5 py-1 md:p-4 rounded-2xl border border-transparent w-full max-w-[480px] cursor-default ${isRightAligned ? 'flex-row lg:flex-row-reverse text-left lg:text-right mr-auto lg:mr-0 lg:ml-auto' : 'flex-row text-left mr-auto'} ${textClass} ${className}`}>
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shrink-0 ${iconBg} ${iconColor} [&>svg]:w-5 [&>svg]:h-5 md:[&>svg]:w-6 md:[&>svg]:h-6 transition-transform duration-500`}>
                {icon}
            </div>
            <div className="flex flex-col flex-1 mt-1 md:mt-1">
                <h4 className="text-base md:text-[17px] font-semibold uppercase tracking-tight mb-1 md:mb-1.5 text-stone-800 leading-[1.3] lg:whitespace-nowrap">{title}</h4>
                <p className="text-sm md:text-base font-medium text-stone-500/90 leading-relaxed">{desc}</p>
            </div>
        </div>
    );
};

const ProductExpertsSection = () => {
    const sectionRef = useRef(null);
    const imageRef = useRef(null);

    React.useEffect(() => {
        if (!window.gsap) return;
        const ctx = window.gsap.context(() => {
            const tl = window.gsap.timeline({
                scrollTrigger: {
                    trigger: '.anatomy-grid',
                    start: 'top 65%'
                }
            });

            tl.fromTo(imageRef.current,
                { y: 100, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
                0
            );

            if (window.innerWidth >= 1024) {
                tl.fromTo('.anatomy-text-left',
                    { x: 25, opacity: 0 },
                    { x: 0, opacity: 1, stagger: 0.1, duration: 0.5, ease: "power2.out" },
                    0.15
                );

                tl.fromTo('.anatomy-text-right',
                    { x: -25, opacity: 0 },
                    { x: 0, opacity: 1, stagger: 0.1, duration: 0.5, ease: "power2.out" },
                    0.15
                );
            } else {
                tl.fromTo('.anatomy-feature-item',
                    { x: -30, opacity: 0 },
                    { x: 0, opacity: 1, stagger: 0.1, duration: 0.5, ease: "power3.out" },
                    0.15
                );
            }

        }, sectionRef);
        return () => ctx.revert();
    }, []);

    return (
        <section className="pt-10 pb-24 md:py-24 lg:py-32 bg-[#FCFBF9] overflow-hidden" ref={sectionRef}>
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">

                <RevealOnScroll className="text-center mt-0 mb-8 md:mb-12">
                    <Label className="text-[#f2663b] font-bold tracking-[0.15em] mb-3">Designed by Product Experts</Label>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tighter text-[#2A2422] leading-[1.1] max-w-4xl mx-auto text-balance">
                        From Apple, Kohler, and Instant Pot, who are obsessed with water and health.
                    </h2>
                </RevealOnScroll>

                <div className="anatomy-grid max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-x-12 xl:gap-x-20 gap-y-10 lg:gap-y-0 items-center relative">
                    {/* Left: Image */}
                    <div className="flex flex-col items-center justify-center relative py-2 anatomy-hardware w-full max-w-[480px] md:max-w-[560px] mx-auto lg:mx-0">
                        <div className="w-full aspect-[0.78/1] md:aspect-[0.85/1] lg:aspect-[0.88/1] relative z-10 rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-stone-200/50 bg-stone-50" ref={imageRef}>
                            <img
                                src="https://res.cloudinary.com/dsyxtnpgm/image/upload/q_auto,f_auto/v1772179854/Generated_Image_February_27_2026_-_4_03PM_gftqpo.webp"
                                alt="Viven Unified System"
                                loading="lazy"
                                className="w-full h-full object-cover object-center hidden md:block"
                            />
                            <img
                                src="https://res.cloudinary.com/dsyxtnpgm/image/upload/w_600,q_auto,f_auto/v1773813226/mobileproduct_kcugpz.webp"
                                alt="Viven Unified System"
                                loading="lazy"
                                className="w-full h-full object-cover object-center md:hidden"
                            />
                        </div>
                    </div>

                    {/* Right: Features */}
                    <div className="flex flex-col justify-center gap-y-8 lg:gap-y-12 h-full">
                        <AnatomyFeature align="left" icon={<Icons.Box />} title="ZERO PLASTIC LEACHING." desc="Construction featuring food-grade internals to guarantee purity." />
                        <AnatomyFeature align="left" icon={<Icons.Waves />} title="High flow. Instant water." desc="No waiting for pitchers & RO system to fill up." />
                        <AnatomyFeature align="left" icon={<Icons.Refresh />} title="100% EFFICIENT. ZERO WASTE." desc="Unlike RO systems, Viven preserves every single drop of water." />
                        <AnatomyFeature align="left" icon={<Icons.Smartphone />} title="REAL-TIME APP TRACKING." desc="Smart app monitoring for filter life and automated reordering." />
                    </div>
                </div>

                <div className="mt-10 flex flex-col items-center justify-center px-4">
                    <a href="#reserve" className="shrink-0">
                        <Button variant="primary" className="h-12 md:h-14 px-10 md:px-12 text-xs md:text-sm">
                            Reserve the Kitchen Faucet+
                        </Button>
                    </a>
                </div>
            </div>
        </section>
    );
};

export default ProductExpertsSection;
